import { useEffect, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  api,
  type HabitacionesResponse,
  type Alert,
  type ChainDailyPoint,
  type DiamondOrigen,
  type DiamondYearResponse,
  type GrowthRow,
  type KpiValue,
  type MarketsResponse,
  type MonthCloseProjection,
  type OperacionResponse,
  type Sede,
  type SummaryResponse,
} from '../lib/api.js';
import { useFilters, sedesParam } from '../lib/filters.js';
import { chartWindows, timeframeSpec } from '../lib/timeframe.js';
import { type KpiId, type SeccionId } from '../lib/vista.js';
import { bucketChain } from '../lib/chartData.js';
import { KpiCard } from '../components/KpiCard.js';
import { VistaPicker } from '../components/VistaPicker.js';
import { Filters } from '../components/Filters.js';
import { SavedViews } from '../components/SavedViews.js';
import { AnomaliesPanel } from '../components/AnomaliesPanel.js';
import { InsightsPanel } from '../components/InsightsPanel.js';
import { MonthlyTotalsTable } from '../components/MonthlyTotalsTable.js';
import { CuentasPanel } from '../components/CuentasPanel.js';
import { CanalesPanel } from '../components/CanalesPanel.js';
import { HabitacionesPanel } from '../components/HabitacionesPanel.js';
import { MarketsPanel } from '../components/MarketsPanel.js';
import { OperacionPanel } from '../components/OperacionPanel.js';
import { OcupacionDiaPanel } from '../components/OcupacionDiaPanel.js';
import { ChartCard, AXIS, GRID, TOOLTIP_STYLE } from '../components/charts/ChartCard.js';
import { DiamondMixChart } from '../components/charts/DiamondMixChart.js';
import { DiamondMonthlyChart } from '../components/charts/DiamondMonthlyChart.js';
import { ChannelDonut } from '../components/charts/ChannelDonut.js';
import { YtdCumulativeChart } from '../components/charts/YtdCumulativeChart.js';
import { SedeRankingChart } from '../components/charts/SedeRankingChart.js';
import { AdrRevparChart } from '../components/charts/AdrRevparChart.js';
import { MonthlyYoYChart } from '../components/charts/MonthlyYoYChart.js';
import { OccupancyHeatmap } from '../components/charts/OccupancyHeatmap.js';
import { ProjectionFanChart } from '../components/charts/ProjectionFanChart.js';
import { fmtBs, fmtInt, fmtPct, fmtUsd } from '../lib/format.js';

const r1 = (x: number) => Math.round(x * 10) / 10;
const r2 = (x: number) => Math.round(x * 100) / 100;

const MES_CORTO = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

/**
 * Nota de procedencia del dato Diamond. Donde no hay carga diaria manda el libro
 * mensual (Excel de Noches Diamond); decirlo evita leer un respaldo mensual como
 * si fuera el cierre diario.
 */
function origenHint(o: DiamondOrigen): string | undefined {
  if (o === 'mensual') return 'Fuente: libro mensual (Excel)';
  if (o === 'mixto') return 'Fuente: diario + libro mensual';
  return undefined;
}

/** Lo que hace falta para pintar una tarjeta KPI (una por id del catálogo). */
interface KpiDef {
  label: string;
  kpi: KpiValue | undefined;
  format: (v: number | null | undefined) => string;
  hint?: string;
}

interface Loaded {
  summary: SummaryResponse;
  trend: { label: string; occ: number | null }[];
  diamondMix: { label: string; diamond: number; house: number; comp: number }[];
  adrRevpar: { label: string; adr: number | null; revpar: number | null }[];
  yoyRevenue: { label: string; current: number | null; prior: number | null }[];
  ytd: { label: string; current: number; prior: number }[];
  heatmap: { fecha: string; occPct: number | null }[];
  ranking: { codigo: string; value: number }[];
  growth: GrowthRow[];
  anomalies: Alert[];
  projection: MonthCloseProjection | null;
  diamond: DiamondYearResponse;
  habitaciones: HabitacionesResponse;
  markets: MarketsResponse;
  operacion: OperacionResponse;
  anio: number;
  hasPrior: boolean;
}

export function Dashboard() {
  const [filters] = useFilters();
  const [sedes, setSedes] = useState<Sede[]>([]);
  const [data, setData] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .sedes()
      .then((r) => setSedes(r.sedes))
      .catch(() => setSedes([]));
  }, []);

  const tf = filters.timeframe;
  const key = `${filters.from}|${filters.to}|${filters.sedes.join(',')}|${filters.compare}|${tf}`;
  useEffect(() => {
    const sedesP = sedesParam(filters);
    const anio = Number(filters.to.slice(0, 4));
    const mes = Number(filters.to.slice(5, 7));
    const win = chartWindows(filters.to, tf);
    setLoading(true);
    setError(null);
    Promise.all([
      api.summary({ from: filters.from, to: filters.to, sedes: sedesP, compare: filters.compare }),
      api.occupancyTrend({ from: filters.from, to: filters.to, sedes: sedesP }),
      api.chainSeries({ from: win.cur.from, to: win.cur.to, sedes: sedesP }),
      win.hasPrior
        ? api.chainSeries({ from: win.prev.from, to: win.prev.to, sedes: sedesP })
        : Promise.resolve({ range: win.prev, points: [] as ChainDailyPoint[] }),
      api.growth({ anio, sedes: sedesP }),
      api.anomalies(),
      api.projectionsMonth({ anio, mes, sedes: sedesP, metrics: 'totalRevenueBs' }),
      api.diamondYear({ anio, sedes: sedesP }),
      api.habitaciones({ from: filters.from, to: filters.to, sedes: sedesP }),
      api.markets({ from: filters.from, to: filters.to, sedes: sedesP }),
      api.operacion({ from: filters.from, to: filters.to, sedes: sedesP }),
    ])
      .then(
        ([
          summary,
          heatmap,
          chainCur,
          chainPrev,
          growth,
          anomalies,
          proj,
          diamond,
          habitaciones,
          markets,
          operacion,
        ]) => {
          const cur = bucketChain(chainCur.points, tf, win.cur.from);
          const prev = win.hasPrior ? bucketChain(chainPrev.points, tf, win.prev.from) : [];
          const prevByPeriod = new Map(prev.map((b) => [b.period, b]));

          let runCur = 0;
          let runPrior = 0;
          const ytd = cur.map((b) => {
            runCur += b.totalRevenueUsd;
            runPrior += prevByPeriod.get(b.period)?.totalRevenueUsd ?? 0;
            return { label: b.label, current: r2(runCur), prior: r2(runPrior) };
          });

          setData({
            summary,
            habitaciones,
            markets,
            operacion,
            trend: cur.map((b) => ({
              label: b.label,
              occ: b.occPct == null ? null : r1(b.occPct * 100),
            })),
            diamondMix: cur.map((b) => ({
              label: b.label,
              diamond: b.diamond,
              house: b.house,
              comp: b.comp,
            })),
            adrRevpar: cur.map((b) => ({ label: b.label, adr: b.adrUsd, revpar: b.revparUsd })),
            yoyRevenue: cur.map((b) => ({
              label: b.label,
              current: b.totalRevenueUsd,
              prior: prevByPeriod.get(b.period)?.totalRevenueUsd ?? null,
            })),
            ytd,
            heatmap: heatmap.points,
            ranking: growth.rows.map((row) => ({
              codigo: row.codigo,
              value: row.totalRevenueUsd.current ?? 0,
            })),
            growth: growth.rows,
            anomalies: anomalies.alerts,
            projection: proj.projections[0] ?? null,
            diamond,
            anio,
            hasPrior: win.hasPrior,
          });
        },
      )
      .catch((e) => setError(e instanceof Error ? e.message : 'Error cargando el dashboard'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const cards = data?.summary.cards ?? {};
  const isBs = filters.currency === 'BS';
  /** ¿Está visible esta área? Lo que no esté en el catálogo no se puede ocultar. */
  const ver = (id: SeccionId) => filters.secciones.includes(id);
  const desc = timeframeSpec(tf).desc;
  const hasChain = !!data && data.diamondMix.length > 0;
  const emptyMsg = <p className="text-sm text-slate-500">Sin datos para el período.</p>;

  // --- Diamond -------------------------------------------------------------
  const diamondHint = origenHint(data?.summary.diamondOrigen ?? null);
  const dYear = data?.diamond;
  const diamondMensual = (dYear?.months ?? []).map((m, i) => ({
    label: MES_CORTO[i]!,
    current: m.diamond,
    prior: dYear?.prior[i]?.diamond ?? null,
  }));
  const hayDiamondMensual = diamondMensual.some((r) => r.current != null || r.prior != null);
  // El mix diario solo existe donde hay carga diaria con Diamond; si no, se
  // muestra el mismo desglose en grano mensual (el libro trae Comp y House).
  const mixDiario = (data?.diamondMix ?? []).filter((b) => b.diamond || b.house || b.comp);
  const mixMensual = (dYear?.months ?? [])
    .map((m, i) => ({
      label: MES_CORTO[i]!,
      diamond: m.diamond ?? 0,
      house: m.house ?? 0,
      comp: m.comp ?? 0,
      vacio: m.origen == null,
    }))
    .filter((m) => !m.vacio);
  const usarMixMensual = mixDiario.length === 0 && mixMensual.length > 0;

  const hab = data?.habitaciones ?? null;

  // --- Tarjetas KPI --------------------------------------------------------
  // Una entrada por id del catálogo (lib/vista.ts); el usuario elige cuáles se
  // pintan con el selector. La etiqueta se arma aquí porque depende de la
  // moneda activa.
  const kpiDefs: Record<KpiId, KpiDef> = {
    occ: { label: 'Ocupación', kpi: cards.occPct, format: fmtPct },
    adr: { label: 'ADR (USD)', kpi: cards.adrUsd, format: fmtUsd },
    revpar: { label: 'RevPAR (USD)', kpi: cards.revparUsd, format: fmtUsd },
    trevpar: { label: 'TRevPAR (USD)', kpi: cards.trevparUsd, format: fmtUsd },
    ingresoTotal: {
      label: isBs ? 'Ingreso total (Bs)' : 'Ingreso total (USD)',
      kpi: isBs ? cards.totalRevenueBs : cards.totalRevenueUsd,
      format: isBs ? fmtBs : fmtUsd,
    },
    ingresoHab: {
      label: 'Ingreso habitaciones (USD)',
      kpi: cards.roomRevenueUsd,
      format: fmtUsd,
    },
    diamond: {
      label: 'Noches Diamond',
      kpi: cards.nochesDiamond,
      format: fmtInt,
      hint: diamondHint,
    },
    diamondShare: {
      label: '% Diamond',
      kpi: cards.diamondShare,
      format: fmtPct,
      hint: diamondHint,
    },
    // El valor son las NOCHES del tipo líder; cuál es va en el hint, porque la
    // tarjeta muestra un número y el nombre cambia con el rango y las sedes.
    tipoLider: {
      label: 'Tipo de habitación líder',
      kpi: hab?.cards.nochesTipoLider,
      format: fmtInt,
      hint: hab?.lider
        ? `${hab.lider.roomType} · ${hab.lider.descripcion ?? ''}`.trim() +
          (hab.lider.share == null ? '' : ` · ${fmtPct(hab.lider.share * 100)} de lo reportado`)
        : 'Sin reporte por tipo de habitación cargado',
    },
  };

  return (
    <div className="space-y-5">
      <SavedViews />
      <Filters sedes={sedes} />

      {loading && <p className="text-slate-400">Cargando…</p>}
      {error && <p className="rounded-lg bg-rose-950/60 px-3 py-2 text-rose-300">{error}</p>}

      {data && (
        <>
          <div className="flex items-center justify-end">
            <VistaPicker />
          </div>

          {filters.kpis.length === 0 && filters.secciones.length === 0 && (
            <p className="text-sm text-slate-500">Todo oculto. Elige qué mostrar en «Vista».</p>
          )}

          {filters.kpis.length > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {filters.kpis.map((id) => {
                const d = kpiDefs[id];
                return (
                  <KpiCard key={id} label={d.label} kpi={d.kpi} format={d.format} hint={d.hint} />
                );
              })}
            </div>
          )}

          {ver('ocupacionDia') && <OcupacionDiaPanel sedesParam={sedesParam(filters)} />}

          <div className="grid gap-4 lg:grid-cols-2">
            {ver('ocupacion') && (
              <ChartCard title={`Tendencia de ocupación (cadena) · ${desc}`}>
                {data.trend.length === 0 ? (
                  emptyMsg
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={data.trend} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                      <XAxis dataKey="label" tick={AXIS} />
                      <YAxis tick={AXIS} unit="%" domain={[0, 100]} />
                      <Tooltip
                        contentStyle={TOOLTIP_STYLE}
                        formatter={(v: number) => [`${v}%`, 'Ocupación']}
                      />
                      <Line
                        type="monotone"
                        dataKey="occ"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            )}

            {ver('diamondMix') && (
              <ChartCard
                title={
                  usarMixMensual
                    ? `Diamond / House / Comp · por mes ${data.anio}`
                    : `Diamond / House / Comp · ${desc}`
                }
              >
                {usarMixMensual ? (
                  <>
                    <DiamondMixChart data={mixMensual} />
                    <p className="mt-2 text-xs text-slate-500">
                      Sin carga diaria de Diamond en el período: se muestra el grano mensual del
                      libro Excel.
                    </p>
                  </>
                ) : hasChain ? (
                  <DiamondMixChart data={data.diamondMix} />
                ) : (
                  emptyMsg
                )}
              </ChartCard>
            )}

            {ver('diamondMensual') && (
              <ChartCard title={`Noches Diamond por mes · ${data.anio} vs ${data.anio - 1}`}>
                {hayDiamondMensual ? (
                  <>
                    <DiamondMonthlyChart data={diamondMensual} anio={data.anio} />
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span>
                        YTD a {MES_CORTO[data.diamond.hastaMes - 1]}:{' '}
                        <span className="text-slate-300">{fmtInt(data.diamond.total)}</span> vs{' '}
                        <span className="text-slate-300">{fmtInt(data.diamond.totalPrior)}</span> en{' '}
                        {data.anio - 1}
                      </span>
                      {origenHint(data.diamond.origen) && (
                        <span>{origenHint(data.diamond.origen)}</span>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-500">
                    Sin noches Diamond para {data.anio}. Se alimentan del reporte diario o del libro
                    mensual (Excel) que se sube en Ingesta.
                  </p>
                )}
              </ChartCard>
            )}

            {ver('mixIngreso') && (
              <ChartCard title="Mix de canal (ingreso)">
                <ChannelDonut mix={data.summary.current.segmentMix} />
              </ChartCard>
            )}

            {ver('ytd') && data.hasPrior && (
              <ChartCard title={`Ingreso acumulado (USD) vs año previo · ${desc}`}>
                {hasChain ? <YtdCumulativeChart data={data.ytd} /> : emptyMsg}
              </ChartCard>
            )}

            {ver('rankingSedes') && (
              <ChartCard title="Ranking de sedes (ingreso USD)">
                <SedeRankingChart data={data.ranking} />
              </ChartCard>
            )}

            {ver('adrRevpar') && (
              <ChartCard title={`ADR y RevPAR (USD) · ${desc}`}>
                {hasChain ? <AdrRevparChart data={data.adrRevpar} /> : emptyMsg}
              </ChartCard>
            )}

            {ver('ingresoYoY') && (
              <ChartCard
                title={
                  data.hasPrior
                    ? `Ingreso vs ${data.anio - 1} · ${desc}`
                    : `Ingreso por año · ${desc}`
                }
              >
                {hasChain ? (
                  <MonthlyYoYChart
                    data={data.yoyRevenue}
                    anio={data.anio}
                    showPrior={data.hasPrior}
                  />
                ) : (
                  emptyMsg
                )}
              </ChartCard>
            )}

            {ver('heatmap') && (
              <ChartCard title="Heatmap de ocupación (día × semana)">
                <OccupancyHeatmap points={data.heatmap} />
              </ChartCard>
            )}

            {ver('proyeccion') && data.projection && (
              <ChartCard title="Proyección de cierre (ingreso total Bs)">
                <ProjectionFanChart p={data.projection} />
              </ChartCard>
            )}

            {ver('anomalias') && <AnomaliesPanel alerts={data.anomalies} />}
          </div>

          {ver('totalizacion') && (
            <MonthlyTotalsTable
              anio={data.anio}
              sedesParam={sedesParam(filters)}
              sedeList={sedes}
              currency={filters.currency}
            />
          )}

          {ver('habitaciones') && <HabitacionesPanel data={data.habitaciones} />}

          {ver('markets') && <MarketsPanel data={data.markets} />}

          {ver('operacion') && <OperacionPanel data={data.operacion} />}

          {ver('canales') && <CanalesPanel anio={data.anio} sedesParam={sedesParam(filters)} />}

          {ver('cuentas') && (
            <CuentasPanel anio={data.anio} sedesParam={sedesParam(filters)} sedeList={sedes} />
          )}

          {ver('insights') && (
            <InsightsPanel from={filters.from} to={filters.to} sedes={sedesParam(filters)} />
          )}
        </>
      )}
    </div>
  );
}
